import type React from "react";
import { useId, useState } from "react";
import { useLocation, useSearchParams } from "wouter";
import { apiService } from "@/api/service";
import LogoSymbol from "@/assets/logo-symbol-transparent-79x96.png";
import LogoWordmark from "@/assets/logo-wordmark-256x48.png";
import { useToast } from "@/components";
import Button from "@/components/ui/Button";
import { validatePassword } from "@/utils/validation";

function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useToast();
  const [, setLocation] = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate passwords
    const validation = validatePassword(password, confirmPassword);
    if (!validation.valid) {
      showError({
        title: "Erro de Validação",
        message: validation.error,
      });
      setIsLoading(false);
      return;
    }

    if (!token) {
      showError({
        title: "Erro",
        message: "Token de redefinição inválido ou expirado.",
      });
      setIsLoading(false);
      return;
    }

    try {
      // Reset password using the token
      await apiService.auth.resetPassword(token, password);

      showSuccess({
        title: "Senha Redefinida",
        message: "Sua senha foi redefinida com sucesso! Você será redirecionado para o login.",
      });

      // Redirect to login page
      setTimeout(() => {
        setLocation("/login");
      }, 2000);
    } catch (err) {
      if (err instanceof Error) {
        showError({
          title: "Erro ao Redefinir Senha",
          message: err.message || "Não foi possível redefinir a senha. Tente novamente.",
        });
      } else {
        showError({
          title: "Erro ao Redefinir Senha",
          message: "Não foi possível redefinir a senha. Tente novamente.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const passwordId = useId();
  const confirmPasswordId = useId();

  if (!token) {
    return (
      <div
        className="h-full overflow-y-auto px-4 flex flex-col items-center justify-center"
        style={{
          background: "linear-gradient(0deg, #18181c 0%, #121b23 40%, #042438 100%)",
        }}
      >
        <div className="w-full max-w-md mt-4">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Link Inválido</h2>
            <p className="text-gray-600 mb-6">O link de redefinição de senha é inválido ou expirou.</p>
            <Button variant="primary" onClick={() => setLocation("/forgot-password")}>
              Solicitar Novo Link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-full overflow-y-auto px-4 flex flex-col items-center justify-center"
      style={{
        background: "linear-gradient(0deg, #18181c 0%, #121b23 40%, #042438 100%)",
      }}
    >
      <div className="w-full max-w-md mt-4">
        {/* Logo and Header */}
        <div className="text-center mb-4">
          <div className="mx-auto flex flex-col items-center text-center">
            <img src={LogoWordmark} alt="Irriga+ Wordmark" className="h-8 mb-2" />
          </div>
          <p className="text-gray-50">Sistema de Controle de Irrigação</p>
        </div>

        {/* Reset Password Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 relative">
          <img src={LogoSymbol} alt="Description" className="absolute top-4 right-4 h-16 object-cover" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">Nova Senha</h2>
          <p className="text-gray-600 text-center mb-6">Digite sua nova senha abaixo.</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor={passwordId}>
                Nova Senha
              </label>
              <input
                id={passwordId}
                type="password"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-colors"
                placeholder="Digite sua nova senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor={confirmPasswordId}>
                Confirmar Nova Senha
              </label>
              <input
                id={confirmPasswordId}
                type="password"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-colors"
                placeholder="Confirme sua nova senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              className="w-full shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              loading={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    role="img"
                  >
                    <title>Redefinindo</title>
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Redefinindo...
                </div>
              ) : (
                "Redefinir Senha"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Lembrou sua senha?{" "}
              <Button type="button" variant="ghost" className="font-medium" onClick={() => setLocation("/login")}>
                Fazer login
              </Button>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-500">
          <p>Sistema de Gestão de Irrigação Automatizada</p>
          <p className="mt-1">Para propriedades rurais de pequeno e médio porte</p>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
